// ────────────────────────────────────────────────────────────────────────────
// How an order's status looks, in one place.
//
// The list and the detail header show the same fact, and they were about to
// show it in two different palettes: these maps lived privately in
// `OrdersPage`, so the header would have needed a second copy. A status whose
// colour changes between two screens is a status an operator has to re-learn.
//
// Constants only — the pill that renders them is `components/OrderStatusPill`.
// Exporting both from one module trips the fast-refresh lint rule.
// ────────────────────────────────────────────────────────────────────────────
import type { OrderStatusValue } from '@my/tms-api';

/** Chip order — the chain, then the escape. Also the filter-chip order. */
export const ORDER_STATUSES: OrderStatusValue[] = [
  'Draft',
  'Open',
  'InProgress',
  'Completed',
  'Cancelled',
];

export const ORDER_STATUS_LABEL: Record<OrderStatusValue, string> = {
  Draft: 'Draft',
  Open: 'Open',
  InProgress: 'In Progress',
  Completed: 'Completed',
  Cancelled: 'Cancelled',
};

export const ORDER_STATUS_DOT: Record<OrderStatusValue, string> = {
  Draft: 'bg-slate-400',
  Open: 'bg-blue-500',
  InProgress: 'bg-amber-500',
  Completed: 'bg-green-500',
  Cancelled: 'bg-red-500',
};

/**
 * Text-only colouring, for surfaces that show the status as plain type rather
 * than a chip — the order header sits it beside Code / Currency / Payment and
 * has to match their weight, so the colour is all that is left to carry the
 * state.
 */
export const ORDER_STATUS_TEXT: Record<OrderStatusValue, string> = {
  Draft: 'text-slate-900',
  Open: 'text-blue-700',
  InProgress: 'text-amber-700',
  Completed: 'text-green-700',
  Cancelled: 'text-red-700',
};

export const ORDER_STATUS_BADGE: Record<OrderStatusValue, string> = {
  Draft: 'bg-slate-100 text-slate-700 ring-slate-300',
  Open: 'bg-blue-50 text-blue-700 ring-blue-200',
  InProgress: 'bg-amber-50 text-amber-700 ring-amber-200',
  Completed: 'bg-green-50 text-green-700 ring-green-200',
  Cancelled: 'bg-red-50 text-red-700 ring-red-200',
};
